import React, { useEffect, useState } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import { Claim } from '@dataTypes/claimFeature.types'
import InputField from '@components/common/InputField/InputField.component'
import { Provider } from '@dataTypes/provider.types'

type ClaimDetailsTabProps = {
  selectedClaim: Claim
  insurance: {
    data: any[]
    selected: any
    handleSelect: (insurancePlanId: string) => void
  }
  provider: {
    data: Provider[]
    selected: {
      id: string | null
      value: string
    }
    handleSelect: (provider: Provider) => void
    handleNameChange: (value: string) => void
  }
  onChangeClaim: (updatedClaim: Claim) => void
}

const ClaimDetailsTab: React.FC<ClaimDetailsTabProps> = ({
  selectedClaim,
  insurance,
  provider,
  onChangeClaim,
}) => {
  const [localClaim, setLocalClaim] = useState<Claim | null>(selectedClaim)

  useEffect(() => {
    setLocalClaim(selectedClaim)
  }, [selectedClaim])

  if (!localClaim) return <p>Loading claim details...</p>

  const handleFormChange = (field: keyof Claim, value: string): void => {
    setLocalClaim((prevClaim) => {
      if (!prevClaim) return prevClaim
      const updatedClaim = { ...prevClaim, [field]: value }
      onChangeClaim(updatedClaim)
      return updatedClaim
    })
  }

  return (
    <div>
      {/* Provider info */}
      <div className="mb-3">
        <h6>Provider info</h6>
        <Row className="gx-2">
          <Col>
            <Form.Group controlId="formEntityType">
              <Form.Label>Entity type</Form.Label>
              <Form.Select
                value={localClaim.entityType || ''}
                onChange={(e) => handleFormChange('entityType', e.target.value)}
              >
                <option value="">Select entity type</option>
                <option value="Individual">Individual</option>
                <option value="Organization">Organization</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <InputField
              label="Provider name"
              value={provider.selected?.value ?? ''}
              placeholder="Enter name"
              onChange={(e) => provider.handleNameChange(e.target.value)}
            />
            {provider.data.length > 0 && (
              <Col md={6}>
                <ul className="provider-list">
                  {provider.data.map((x) => (
                    <li
                      key={x.provider_id}
                      onClick={() => provider.handleSelect(x)}
                      className="provider-dropdown-item"
                    >
                      {x.first_name} {x.last_name}
                    </li>
                  ))}
                </ul>
              </Col>
            )}
          </Col>
          <Col>
            <InputField
              type="text"
              label="Tax ID"
              value={localClaim.providerTaxId || ''}
              disabled
            />
          </Col>
        </Row>

        <Row className="gx-2 mt-2">
          <Col>
            <Form.Group controlId="formItemType">
              <Form.Label>Item type</Form.Label>
              <Form.Select value={localClaim.claimType || ''} disabled>
                <option value="Claims">Claims</option>
                <option value="Pre-Authorization">Pre-Authorization</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="formInsuranceProvider">
              <Form.Label>Insurance provider</Form.Label>
              <Form.Select
                value={insurance.selected?.insurance_plan_id ?? ''}
                onChange={(e) => insurance.handleSelect(e.target.value)}
              >
                <option value={''}>Select</option>
                {(insurance.data ?? []).map((x) => (
                  <option key={x.insurance_plan_id} value={x.insurance_plan_id}>
                    {x.carrier_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <InputField
              type="text"
              label="Payer code"
              value={localClaim.insuranceCarrierPayerCode || ''}
              disabled
            />
          </Col>
        </Row>
      </div>

      {/* Patient info */}
      <div className="mb-2">
        <h6>Patient info</h6>
        <Row className="gx-2">
          <Col>
            <InputField
              type="text"
              label="Member ID"
              value={localClaim.patientMemberId || ''}
              disabled
            />
          </Col>
          <Col>
            <InputField
              type="text"
              label="Patient"
              value={localClaim.patientName}
              disabled
            />
          </Col>
          <Col>
            <InputField
              type="text"
              label="Date of birth"
              value={localClaim.patientDateOfBirth || ''}
              disabled
            />
          </Col>
        </Row>
      </div>

      {/* Subscriber Info */}
      <div className="mb-3">
        <Row className="gx-2">
          <Col>
            <InputField
              type="text"
              label="Member ID"
              value={localClaim.subscriberId}
              disabled
            />
          </Col>
          <Col>
            <InputField
              type="text"
              label="Subscriber"
              value={localClaim.subscriberName}
              disabled
            />
          </Col>
          <Col>
            <InputField
              type="text"
              label="Date of birth"
              value={localClaim.subscriberDateOfBirth || ''}
              disabled
            />
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default ClaimDetailsTab
